import { JsonPipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseConfigurableComponent } from '@app/base-config.component';
import { InfoComponent } from '@app/components/global/info/info.component';
import { LoadingComponent } from '@app/components/global/loading/loading.component';
import { BannerComponent } from '@app/components/shared/banner/banner.component';
import { QuestionComponent } from '@app/components/shared/form/question/question.component';
import { InputType } from '@app/enum/input-type';
import { QuestionBase } from '@app/models/question-base';
import { DropDownquestion } from '@app/models/questions/question-dropdown';
import { FormatPipe } from '@app/pipes/format.pipe';
import { CalendarAvailabilityService } from '@app/services/calendarAvailability/calendarAvailability.service';
import { CategoriesService } from '@app/services/categories/categories.service';
import { Availabilities } from '@app/services/fetch/availability';
import { QuestionControlService } from '@app/services/question/question-control.service';
import { SchedulerStepService } from '@app/services/step/scheduler-step.service';

@Component({
  selector: 'afcu-calendar',
  standalone: true,
  providers: [QuestionControlService],
  imports: [QuestionComponent, InfoComponent, LoadingComponent, BannerComponent],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.css'
})
export class CalendarComponent extends BaseConfigurableComponent {

  protected formatPipe = inject(FormatPipe)
  private readonly qcs = inject(QuestionControlService)
  protected schedulerService = inject( SchedulerStepService )
  protected fetchAvailabilities = inject( Availabilities );
  private categoriesService = inject(CategoriesService)
  protected calendarAvailabilityService = inject( CalendarAvailabilityService );
  matchingServiceFound = true;
  
  protected router = inject( Router )

  timezone = ''
  hourDiff = 0

  serviceExist = true
  target:string = ''

  isError = false
  errorBanner = {title: this.getText().calendar.bannerWarning.title ,content: ''} as BannerType

  selectedDay = signal<number>(-1)

  disableDate = computed(() => {
    const month = this.calendarAvailabilityService.month()
    const year = this.calendarAvailabilityService.year()

    const arrayEntries = Array.from(this.calendarAvailabilityService.mapDate().entries())

    
    if(arrayEntries.length > 0 ) {
      const [maxKey, maxVal] = arrayEntries[arrayEntries.length-1]
      const maxMonth = new Date(maxKey.toString().replaceAll('-','/')).getMonth()
      const maxYear = new Date(maxKey.toString().replaceAll('-','/')).getFullYear()

      const [minKey, minVal] = arrayEntries[0]
      const minMonth = new Date(minKey.toString().replaceAll('-','/')).getMonth()
      const minYear = new Date(minKey.toString().replaceAll('-','/')).getFullYear()

      return {next: (maxMonth === month && maxYear === year), prev: (minMonth === month && minYear === year) }
    } 

    return {next: true, prev: true}
  })

  questions: QuestionBase<string>[] = [this.FormLanguage, this.FormBranchStaff]
  inputType : typeof InputType = InputType
  form: FormGroup = this.qcs.toFormGroup(this.questions as QuestionBase<string>[]);

  constructor(override route: ActivatedRoute) {
    super('page')
    this.route.data.subscribe( data => {
      this.target = data['target']
    } )

    effect(() => {
      const options = this.getTimeSlotStaff()

      const now = new Date();

      const branchHour = new Date(now.toLocaleString("en-US",  { timeZone: this.schedulerService.branchSelected().timeZone })).getHours();
      const utcHour = now.getUTCHours()
      this.hourDiff = utcHour-branchHour
      this.timezone = this.getText().calendar.convertTime[this.schedulerService.branchSelected().timeZone]


      if (options.length > 1) {
        const findYou = options.find( staff => staff.value === this.calendarAvailabilityService.staffSelected())
        if(findYou === undefined){
          this.isError = true
          this.calendarAvailabilityService.staffSelected.set('all')
        }
      }
      
    })
  }

  convertTo12HourFormat(time24: string): string {
    const hour = parseInt(time24.slice(0, 2), 10);
    const minute = time24.slice(2, 4);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12; 
    return `${hour12}:${minute} ${period}`;
  }

  selectEvent(event: { function: string; value: string | CategorySelected | Branch }): void {
    if (event.function === 'changeLanguage'){
      this.matchingServiceFound = false
      const originalService = this.categoriesService.services().serviceList.filter( category => { return category.service.name === this.schedulerService.payload()['services']})[0]!
      this.categoriesService.services().serviceList.forEach( service => { 
        if(originalService.service.serviceGroupID === service.service.serviceGroupID){
          if(event.value.toString() == service.service.language.toLowerCase()) {
            this.matchingServiceFound = true
            this.schedulerService.setPayload('services', {description: service.service.name} as CategorySelected)
          }
           
        }
      })
      // 
      this.textService.setLanguage(event.value.toString())
    }else if (event.function === 'selectStaff'){  
      this.isError = false
      this.calendarAvailabilityService.staffSelected.set(event.value as string)
    }
    this.questions = [this.FormLanguage, this.FormBranchStaff]
  }

  checkAvailability(day:  { day: number; month: number, year:number, staffAvailability: StaffAvailability[]; }) {
    if (day.staffAvailability.length) {
      this.calendarAvailabilityService.daySelectedAvailability.set({
        year: day.year,
        date: day.day,
        month: day.month,
        staffAvailability: day.staffAvailability
      })

      this.questions = [this.FormLanguage, this.FormBranchStaff]

      this.selectedDay.set(Number(day.day))
    }
  }

  selectAvailability(time :string) {
    this.schedulerService.payload.update( payload => ({...payload, staff: this.calendarAvailabilityService.staffSelected()}))
    this.schedulerService.setPayload('calendar', {description: new Date(this.calendarAvailabilityService.daySelectedAvailability().year, this.calendarAvailabilityService.daySelectedAvailability().month-1,  this.calendarAvailabilityService.daySelectedAvailability().date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) + ' @ ' + time } as CategorySelected)
    this.router.navigate([`/contact-info`], {queryParamsHandling: 'merge'})
  } 

  getTimeSlotStaff(): InputOption[] {
    const allOption = { 
      text: this.getText().calendar.input.allMember,
      value: "all",
      selected: this.calendarAvailabilityService.staffSelected() === 'all'
    };

    const selected = this.calendarAvailabilityService.daySelectedAvailability();
    const staffAvailability = selected?.staffAvailability;

    if (Array.isArray(staffAvailability) && staffAvailability.length > 0) {
      const staffMembers = [
        ...new Map(
          staffAvailability
            .flatMap(timeSlotStaff => timeSlotStaff.staffMembers || [])
            .map(member => [member.staffID, member])
        ).values()
      ].map(member => ({
        text: member.name,
        value: member.staffID.toString(),
        selected: this.calendarAvailabilityService.staffSelected() === member.staffID.toString()
      }));

      return [allOption, ...staffMembers];
    }
    return [allOption];
  }

  get FormBranchStaff() {
    if(this.schedulerService.payload()['services'] === undefined)
      this.router.navigate(['services'], { replaceUrl: true })

    const serviceInfo = this.categoriesService.ServiceInfo(this.schedulerService.payload()['services'])

    return new DropDownquestion ({
      key: 'branchStaff',
      options: this.getTimeSlotStaff(),
      externalFunction: "selectStaff",
      disable: (Object.keys(serviceInfo).length === 0 ) ? true : !serviceInfo.isSelectable,
      order: 1
    })
  }

  get FormLanguage() {
    return new DropDownquestion ({
      key: 'language',
      options: this.textService.DropDownLanguage,
      externalFunction: "changeLanguage",
      order: 2
    })
  }

  changeMonth (direction:boolean) {
    const delta = direction ? 1 : -1;

    this.calendarAvailabilityService.month.update(val => {
      let newMonth = val + delta;
      if (newMonth > 11) newMonth = 0;
      if (newMonth < 0) newMonth = 11;
      return newMonth;
    });

    const newMonth = this.calendarAvailabilityService.month();
    if ((direction && newMonth === 0) || (!direction && newMonth === 11)) {
      this.calendarAvailabilityService.year.update(val => val + delta);
    }
  }

}
